import { RouteRecordRaw } from 'vue-router';
import { requireAuth, setDayjsTimezone } from '@/common/router/navigationGuard';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';
import { VIEW_MODE } from '@/common/define/common.define';
import { extractAccessibleRoutes } from '@/common/auth/auth.utils';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { MaxgaugeDatabaseSubMenu } from '@/common/utils';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import {
  requireDomainTargetOrTutorial,
  requireInstances,
  validateDatabaseOverviewPath,
} from '@/common/router/authGuard';

const PREFIX_MYSQL = 'mysql_';

const mysqlMenu: RouteRecordRaw[] = [
  {
    path: '/mysql/',
    name: `${PREFIX_MYSQL}Real Time Monitoring`,
    component: BaseViewport,
    children: [
      {
        path: 'multiView',
        name: `${PREFIX_MYSQL}Multi View`,
        component: () => import('@/mysql/views/multiView/MultiView.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'singleView',
        name: `${PREFIX_MYSQL}Single View`,
        component: () => import('@/mysql/views/singleView/SingleView.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [
      requireAuth,
      () => requireDomainTargetOrTutorial('database'),
      requireInstances,
      setDayjsTimezone,
      validateDatabaseOverviewPath,
    ],
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD,
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.REALTIME,
    },
  },
  {
    path: '/mysql/',
    name: `${PREFIX_MYSQL}SQL Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'topNAnalysis',
        name: `${PREFIX_MYSQL}Top N Analysis`,
        component: () => import('@/mysql/views/topNAnalysis/TopNAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'searchSql',
        name: `${PREFIX_MYSQL}Search SQL`,
        component: () => import('@/mysql/views/searchSql/SearchSql.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, () => requireDomainTargetOrTutorial('database')],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_MYSQL_PA,
        [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MYSQL_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/mysql/',
    name: `${PREFIX_MYSQL}Session Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'searchSession',
        name: `${PREFIX_MYSQL}Search Session`,
        component: () => import('@/database/views/searchSession/mysql/SearchSession.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, requireInstances],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_MYSQL_PA,
        [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MYSQL_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/mysql/',
    name: `${PREFIX_MYSQL}Performance Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'trendAnalysis',
        name: `${PREFIX_MYSQL}Trend Analysis`,
        component: () => import('@/database/views/trendAnalysis/mysql/TrendAnalysis.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
      {
        path: 'parameterHistory',
        name: `${PREFIX_MYSQL}Parameter History`,
        component: () => import('@/database/views/parameterHistory/mysql/ParameterHistory.vue'),
        beforeEnter: [requireInstances],
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_MYSQL_PA,
        [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MYSQL_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
  {
    path: '/mysql/',
    name: `${PREFIX_MYSQL}DataSpace Analysis`,
    component: BaseViewport,
    children: [
      {
        path: 'objectSize',
        name: `${PREFIX_MYSQL}Object Size`,
        component: () => import('@/mysql/views/objectSize/ObjectSize.vue'),
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
      },
    ],
    beforeEnter: [requireAuth, requireInstances],
    meta: {
      rolePermission: {
        [VIEW_MODE.EXEMONE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_MYSQL_PA,
        [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MYSQL_PERFORMANCE_ANALYSIS,
      },
      invisibleViewMode: [VIEW_MODE.INTERMAX],
      viewport: VIEWPORT_TYPE.PA,
    },
  },
];

const subMenuMappingMySql = {
  instanceView: `${PREFIX_MYSQL}Real Time Monitoring`,
  sqlAnalysis: `${PREFIX_MYSQL}SQL Analysis`,
  sessionAnalysis: `${PREFIX_MYSQL}Session Analysis`,
  performanceAnalysis: `${PREFIX_MYSQL}Performance Analysis`,
  dataSpaceAnalysis: `${PREFIX_MYSQL}DataSpace Analysis`,
};

const getMysqlNaviMenu = (subMenu?: MaxgaugeDatabaseSubMenu['mysql']) => {
  const filteredMySqlMenu = extractAccessibleRoutes(mysqlMenu);

  if (subMenu && subMenuMappingMySql[subMenu]) {
    return filteredMySqlMenu.filter((menu) => menu.name === subMenuMappingMySql[subMenu]);
  }

  return filteredMySqlMenu.filter((menu) => menu.name !== subMenuMappingMySql.instanceView);
};

export { mysqlMenu, getMysqlNaviMenu, PREFIX_MYSQL };
